/**
 * Admin learner detail — module-by-module progress, attempts, and cert for
 * one learner. Reached from the dashboard table. Self-gates like the rest of
 * the admin pages (401 → login, 403 → notice, 404 → not found).
 */
import type * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { API_BASE_URL, ApiError } from "@/lib/api";
import { type LearnerDetailResponse, getLearnerDetail } from "@/lib/admin-api";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: LearnerDetailResponse }
  | { kind: "forbidden" }
  | { kind: "notfound" }
  | { kind: "error"; message: string };

const AT_RISK_LABEL: Record<string, string> = {
  never_signed_in: "Never signed in",
  stalled: "Stalled",
  capstone_awaiting_grading: "Awaiting grading",
  email_bouncing: "Email bouncing",
};

function redirectToLogin(returnTo: string): void {
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encodeURIComponent(returnTo)}`;
}

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

export function AdminLearnerDetailPage(): React.JSX.Element {
  const { userId = "" } = useParams<{ userId: string }>();
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });

  const refresh = useCallback(async (): Promise<void> => {
    setLoad({ kind: "loading" });
    try {
      const data = await getLearnerDetail(userId);
      setLoad({ kind: "ready", data });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        redirectToLogin(`/admin/learners/${userId}`);
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setLoad({ kind: "forbidden" });
        return;
      }
      if (err instanceof ApiError && err.status === 404) {
        setLoad({ kind: "notfound" });
        return;
      }
      const msg = err instanceof Error ? err.message : "Failed to load learner.";
      setLoad({ kind: "error", message: msg });
    }
  }, [userId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (load.kind === "forbidden") {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <h1 className="text-2xl font-semibold text-foreground">Admin access required</h1>
        <p className="mt-4 text-muted-foreground">Sign in with an admin account to continue.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <Link to="/admin" className="text-sm text-muted-foreground hover:text-foreground">
        ← Back to dashboard
      </Link>

      {load.kind === "loading" ? (
        <p className="mt-8 text-center text-muted-foreground">Loading…</p>
      ) : load.kind === "notfound" ? (
        <p className="mt-8 text-center text-muted-foreground">
          Learner not found, or they have no enrollment.
        </p>
      ) : load.kind === "error" ? (
        <p className="mt-8 text-center text-rose-700">{load.message}</p>
      ) : load.kind === "ready" ? (
        <LearnerDetailBody data={load.data} />
      ) : null}
    </div>
  );
}

function LearnerDetailBody({ data }: { data: LearnerDetailResponse }): React.JSX.Element {
  const l = data.learner;
  return (
    <>
      <header className="mt-4 border-b border-border pb-6">
        <h1 className="text-2xl font-semibold text-foreground">{l.name || l.email}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{l.email}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-foreground">
          <span>Enrollment: {l.enrollment_status}</span>
          <span>·</span>
          <span>{l.signed_in ? "Signed in" : "Not signed in"}</span>
          <span>·</span>
          <span>Last active: {formatDate(l.last_activity_at)}</span>
          <span>·</span>
          <span>
            {l.badge_count ?? 0} badge{(l.badge_count ?? 0) === 1 ? "" : "s"} ·{" "}
            {l.total_xp ?? 0} XP
          </span>
        </div>
        {l.at_risk_reasons.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-1">
            {l.at_risk_reasons.map((reason) => (
              <span
                key={reason}
                className="inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-900"
              >
                {AT_RISK_LABEL[reason] ?? reason}
              </span>
            ))}
          </div>
        ) : null}
      </header>

      <section className="mt-6">
        <h2 className="text-sm font-semibold text-foreground">Modules</h2>
        <div className="mt-3 overflow-x-auto rounded-lg border border-border bg-white shadow-sm">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-background">
              <tr>
                {["#", "Module", "Lessons", "Quiz", "Attempts"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.modules.map((m) => (
                <tr key={m.sequence}>
                  <td className="px-4 py-3 text-sm text-foreground">{m.sequence}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{m.title}</td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    {m.lessons_complete}/{m.lessons_total}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    {m.quiz_passed ? "✓ passed" : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{m.quiz_attempts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div className="rounded-lg border border-border bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-foreground">Exam attempts</h2>
          {data.exam_attempts.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">No attempts.</p>
          ) : (
            <ul className="mt-2 space-y-1 text-sm text-foreground">
              {data.exam_attempts.map((a) => (
                <li key={a.attempt_number}>
                  #{a.attempt_number}: {a.status}
                  {a.score !== null ? ` (${a.score}%)` : ""} · {formatDate(a.submitted_at)}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-lg border border-border bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-foreground">Capstone</h2>
          {data.capstones.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Not started.</p>
          ) : (
            <ul className="mt-2 space-y-1 text-sm text-foreground">
              {data.capstones.map((c) => (
                <li key={c.attempt_number}>
                  #{c.attempt_number}: {c.status}
                  {c.pass_fail ? ` · ${c.pass_fail} (${c.composite_score})` : ""} ·{" "}
                  {c.word_count} words
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-sm text-foreground">
            Certificate: {data.certificate.issued ? "issued" : "not issued"}
            {data.certificate.issued_at ? ` · ${formatDate(data.certificate.issued_at)}` : ""}
          </p>
        </div>
      </section>
    </>
  );
}
